import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useTheme
} from '@mui/material';

import { Reorder, useDragControls } from 'framer-motion';
import BinIcon from '@mui/icons-material/Delete';
import DragIcon from '@mui/icons-material/DragIndicator';
import { sectionDrawSetup } from '../../features/event/eventSlice';

// Separate draggable item component
const DraggableTeamCard = ({ team, index, onRemove }) => {
  const controls = useDragControls();
  const theme = useTheme();

  return (
    <Reorder.Item
      value={team}
      //   whileDrag={{ scale: 1.05, zIndex: 999 }}
      dragListener={false}
      dragControls={controls}>
      <Card
        variant="outlined"
        sx={{
          minHeight: '60px',
          flexShrink: 0,
          padding: 1,
          my: 1,
          border: `1px solid ${theme.palette.primary.main}`,
          borderRadius: '10px'
        }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <IconButton
            size="small"
            onPointerDown={(e) => controls.start(e)}
            sx={{
              cursor: 'grab',
              '&:active': {
                cursor: 'grabbing'
              }
            }}>
            <DragIcon sx={{ color: 'text.primary' }} />
          </IconButton>
          <Typography variant="subtitle1" sx={{ flexGrow: 1, textAlign: 'center' }}>
            {index + 1}. {team.name}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(team);
            }}>
            <BinIcon color="primary" />
          </IconButton>
        </Stack>
      </Card>
    </Reorder.Item>
  );
};

const SetupSectionDraw = ({ event, section, open, onClose }) => {
  const dispatch = useDispatch();
  const loadingDraw = useSelector((state) => state.event.loadingDraw);
  const [teams, setTeams] = React.useState(event?.teams || []);
  const [selectedTeams, setSelectedTeams] = React.useState([]);

  useEffect(() => {
    if (section && section.initialDraw) {
      const selectTeams = [];
      section.initialDraw.forEach((team) => {
        const foundTeam = event.teams.find((t) => t.id === team.id);
        if (foundTeam) {
          selectTeams.push(foundTeam);
        }
      });

      setSelectedTeams(selectTeams);
    }
  }, [event, section]);

  const handleTeamClick = (team) => {
    const isAlreadySelected = selectedTeams.some((selectedTeam) => selectedTeam.id === team.id);

    if (isAlreadySelected) {
      setSelectedTeams(selectedTeams.filter((selectedTeam) => selectedTeam.id !== team.id));
    } else {
      setSelectedTeams([...selectedTeams, team]);
    }
  };

  const handleRemoveTeam = (teamToRemove) => {
    setSelectedTeams(selectedTeams.filter((team) => team.id !== teamToRemove.id));
  };

  const handleAddAllTeams = () => {
    setSelectedTeams([...teams]);
  };

  const handleClearAllTeams = () => {
    setSelectedTeams([]);
  };

  const isTeamSelected = (team) => {
    return selectedTeams.some((selectedTeam) => selectedTeam.id === team.id);
  };

  const onSubmit = () => {
    // Add teams to an array. Team object should look like : { id: team.id, name: team.name, position: index }
    const drawTeams = selectedTeams.map((team, index) => ({
      id: team.id,
      name: team.name,
      position: index + 1
    }));

    dispatch(sectionDrawSetup({ eventId: event.id, sectionId: section.id, teams: drawTeams }));

    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Section Draw Setup - {event?.name}</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>
          Select the teams for the section draw. Teams will be listed below. Please order the teams
          by dragging and dropping in the position.
        </Typography>
        <Box display={'flex'} flexDirection={'row'} gap={2} sx={{ mb: 2 }}>
          <Box flexGrow={1}>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Typography variant="h6" color="textSecondary" sx={{ flexGrow: 1 }}>
                Available Teams
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={handleAddAllTeams}
                disabled={selectedTeams.length === teams.length}>
                Add All
              </Button>
            </Stack>

            <Stack sx={{ maxHeight: '400px', overflowY: 'auto', gap: 1, p: 1 }}>
              {teams.length > 0 ? (
                teams.map((team, index) => (
                  <Card
                    key={team.id || index}
                    variant="outlined"
                    onClick={() => handleTeamClick(team)}
                    sx={{
                      minHeight: '50px',
                      flexShrink: 0,
                      padding: 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      backgroundColor: isTeamSelected(team) ? 'primary.light' : 'background.paper',
                      borderColor: isTeamSelected(team) ? 'primary.main' : 'grey.300',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-1px)',
                        boxShadow: 2
                      },
                      '&:last-child': { marginBottom: 0 }
                    }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: isTeamSelected(team) ? 'primary.contrastText' : 'text.primary',
                        fontWeight: isTeamSelected(team) ? 'bold' : 'normal'
                      }}>
                      {index + 1}. {team.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: isTeamSelected(team) ? 'primary.contrastText' : 'text.secondary'
                      }}>
                      Members: {team.members?.map((member) => member.name).join(', ')}
                    </Typography>
                  </Card>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No players available for this section.
                </Typography>
              )}
            </Stack>
          </Box>
          <Box flexGrow={1}>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Typography variant="h6" color="textSecondary" sx={{ flexGrow: 1 }}>
                Selected Teams ({selectedTeams.length})
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleClearAllTeams}
                disabled={selectedTeams.length === 0}>
                Clear All
              </Button>
            </Stack>

            <Box sx={{ height: '400px' }}>
              <Reorder.Group
                axis="y"
                values={selectedTeams}
                onReorder={setSelectedTeams}
                layoutScroll
                style={{
                  padding: 0,
                  margin: 0,
                  height: '100%',
                  overflowY: 'auto'
                }}>
                {selectedTeams.length > 0 ? (
                  selectedTeams.map((team, index) => (
                    <DraggableTeamCard
                      key={team.id}
                      team={team}
                      index={index}
                      onRemove={handleRemoveTeam}
                    />
                  ))
                ) : (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ p: 2, textAlign: 'center' }}>
                    No teams selected.
                  </Typography>
                )}
              </Reorder.Group>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onSubmit()} color="primary">
          Save
        </Button>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetupSectionDraw;
